// Font loader utility for Google Fonts
const GOOGLE_FONTS_API = "https://fonts.googleapis.com/css2";

// Map of font values to Google Fonts URLs
const googleFonts: Record<string, { family: string; weights?: string[] }> = {
  // Popular Sans-serif fonts (prioritized)
  roboto: {
    family: "Roboto",
    weights: ["100", "300", "400", "500", "700", "900"],
  },
  inter: {
    family: "Inter",
    weights: ["100", "200", "300", "400", "500", "600", "700", "800", "900"],
  },
  montserrat: {
    family: "Montserrat",
    weights: ["100", "200", "300", "400", "500", "600", "700", "800", "900"],
  },
  "open-sans": {
    family: "Open+Sans",
    weights: ["300", "400", "500", "600", "700", "800"],
  },
  "plus-jakarta-sans": {
    family: "Plus+Jakarta+Sans",
    weights: ["200", "300", "400", "500", "600", "700", "800"],
  },

  // Other Sans-serif fonts
  lato: { family: "Lato", weights: ["100", "300", "400", "700", "900"] },
  poppins: {
    family: "Poppins",
    weights: ["100", "200", "300", "400", "500", "600", "700", "800", "900"],
  },
  nunito: {
    family: "Nunito",
    weights: ["200", "300", "400", "500", "600", "700", "800", "900"],
  },
  raleway: {
    family: "Raleway",
    weights: ["100", "200", "300", "400", "500", "600", "700", "800", "900"],
  },
  ubuntu: { family: "Ubuntu", weights: ["300", "400", "500", "700"] },
  oswald: {
    family: "Oswald",
    weights: ["200", "300", "400", "500", "600", "700"],
  },
  "source-sans": {
    family: "Source+Sans+Pro",
    weights: ["200", "300", "400", "600", "700", "900"],
  },
  "noto-sans": {
    family: "Noto+Sans",
    weights: ["100", "200", "300", "400", "500", "600", "700", "800", "900"],
  },

  // Popular Serif fonts (prioritized)
  playfair: {
    family: "Playfair+Display",
    weights: ["400", "500", "600", "700", "800", "900"],
  },
  "dm-serif-display": { family: "DM+Serif+Display", weights: ["400"] },
  merriweather: {
    family: "Merriweather",
    weights: ["300", "400", "700", "900"],
  },

  // Other Serif fonts
  lora: { family: "Lora", weights: ["400", "500", "600", "700"] },
  crimson: { family: "Crimson+Text", weights: ["400", "600", "700"] },
  bitter: {
    family: "Bitter",
    weights: ["100", "200", "300", "400", "500", "600", "700", "800", "900"],
  },
  "libre-baskerville": { family: "Libre+Baskerville", weights: ["400", "700"] },
  "eb-garamond": {
    family: "EB+Garamond",
    weights: ["400", "500", "600", "700", "800"],
  },

  // Monospace fonts
  "source-code": {
    family: "Source+Code+Pro",
    weights: ["200", "300", "400", "500", "600", "700", "900"],
  },
  "fira-code": {
    family: "Fira+Code",
    weights: ["300", "400", "500", "600", "700"],
  },
  "jetbrains-mono": {
    family: "JetBrains+Mono",
    weights: ["100", "200", "300", "400", "500", "600", "700", "800"],
  },
  "ibm-plex-mono": {
    family: "IBM+Plex+Mono",
    weights: ["100", "200", "300", "400", "500", "600", "700"],
  },
  "roboto-mono": {
    family: "Roboto+Mono",
    weights: ["100", "200", "300", "400", "500", "600", "700"],
  },
  "space-mono": { family: "Space+Mono", weights: ["400", "700"] },
  "ubuntu-mono": { family: "Ubuntu+Mono", weights: ["400", "700"] },

  // Display & Script fonts
  "comic-sans": { family: "Comic+Neue", weights: ["400", "700"] },
  "bebas-neue": { family: "Bebas+Neue", weights: ["400"] },
  anton: { family: "Anton", weights: ["400"] },
  righteous: { family: "Righteous", weights: ["400"] },
  pacifico: { family: "Pacifico", weights: ["400"] },
  "dancing-script": {
    family: "Dancing+Script",
    weights: ["400", "500", "600", "700"],
  },
  "great-vibes": { family: "Great+Vibes", weights: ["400"] },
  satisfy: { family: "Satisfy", weights: ["400"] },
  "kaushan-script": { family: "Kaushan+Script", weights: ["400"] },
  allura: { family: "Allura", weights: ["400"] },
};

// Track loaded fonts to avoid duplicates
const loadedFonts = new Set<string>();

// Keys that should not trigger Google Fonts loading
const SKIP_FONT_KEYS = new Set<string>([
  "",
  "sans",
  "sans-serif",
  "serif",
  "mono",
  "monospace",
  "system-ui",
  "script",
  "inherit",
  "initial",
  "unset",
  "default",
  "auto",
  "arial",
  "helvetica",
  "verdana",
  "tahoma",
  "trebuchet ms",
  "gill sans",
  "segoe ui",
  "times",
  "times new roman",
  "georgia",
  "palatino",
  "bookman",
  "garamond",
  "didot",
  "cambria",
  "courier",
  "courier new",
  "lucida console",
  "monaco",
  "consolas",
  "papyrus",
  "copperplate",
]);

function sanitizeFontFamily(value: string): string {
  if (!value) return "";
  const primary = value.split(",")[0]?.trim() ?? "";
  return primary.replace(/^['"]+|['"]+$/g, "");
}

function shouldSkipFont(family: string): boolean {
  if (!family) return true;
  const sanitized = sanitizeFontFamily(family);
  if (!sanitized) return true;
  const normalized = sanitized.toLowerCase();
  if (normalized.startsWith("var(")) return true;
  return SKIP_FONT_KEYS.has(normalized);
}

function toSlugKey(family: string): string {
  return sanitizeFontFamily(family).toLowerCase().replace(/\s+/g, "-");
}

function normalizeWeights(weights?: string[]): string[] | undefined {
  if (!weights || weights.length === 0) return undefined;
  const normalized = weights
    .map((w) => w?.toString().trim().toLowerCase())
    .filter((w): w is string => Boolean(w))
    .map((w) => {
      if (w === "regular" || w === "italic") return "400";
      const numeric = w.replace("italic", "");
      return /^(100|200|300|400|500|600|700|800|900)$/.test(numeric)
        ? numeric
        : null;
    })
    .filter((w): w is string => Boolean(w));

  if (normalized.length === 0) return undefined;
  return Array.from(new Set(normalized));
}

function normalizeSubsets(subsets?: string[]): string[] | undefined {
  if (!subsets || subsets.length === 0) return undefined;
  const normalized = subsets
    .map((s) => s?.toString().trim().toLowerCase())
    .filter((s): s is string => Boolean(s));

  if (normalized.length === 0) return undefined;
  return Array.from(new Set(normalized));
}

function toStringArray(value: unknown): string[] | undefined {
  if (!value) return undefined;
  if (Array.isArray(value)) {
    const arr = value
      .map((item) => (typeof item === "string" ? item.trim() : ""))
      .filter((item): item is string => Boolean(item));
    return arr.length > 0 ? arr : undefined;
  }
  if (typeof value === "string") {
    const arr = value
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
    return arr.length > 0 ? arr : undefined;
  }
  return undefined;
}

// Encode a font family name for CSS2 API (replace spaces with '+')
function encodeFamilyName(family: string): string {
  return family.trim().replace(/\s+/g, "+");
}

/**
 * Load a Google Font dynamically
 */
export function loadGoogleFont(
  fontFamily: string,
  weights?: string[],
  subsets?: string[]
): void {
  const sanitizedFamily = sanitizeFontFamily(fontFamily);

  if (shouldSkipFont(sanitizedFamily)) {
    return;
  }
  if (typeof document === "undefined") return;

  const normalizedWeights = normalizeWeights(weights);
  const normalizedSubsets = normalizeSubsets(subsets);

  // If the font exists in our curated map, use that config (backward compatibility during transition)
  const normalizedKey = sanitizedFamily.toLowerCase();
  const curated =
    googleFonts[normalizedKey] || googleFonts[toSlugKey(sanitizedFamily)];
  if (curated) {
    // Use requested weights if provided, otherwise use curated weights, fallback to ["400"]
    let selected: string[];
    if (normalizedWeights && normalizedWeights.length > 0) {
      // Filter requested weights to only those available for this font
      selected = normalizedWeights.filter(
        (w) => !curated.weights || curated.weights.includes(w)
      );
      // If no valid weights after filtering, use first available weight
      if (
        selected.length === 0 &&
        curated.weights &&
        curated.weights.length > 0
      ) {
        selected = [curated.weights[0]];
      } else if (selected.length === 0) {
        selected = ["400"];
      }
    } else {
      // No weights requested - use curated weights or fallback
      selected = curated.weights || ["400"];
    }

    const fontKey = `${curated.family}:${selected.join(";")}:${(normalizedSubsets || []).join(",")}`;

    // Check if ANY link for this font family already exists
    const baseFontKey = curated.family;
    const anyExistingLink = document.querySelector(
      `link[data-font^="${baseFontKey}:"]`
    );
    if (anyExistingLink) {
      const existingDataFont = anyExistingLink.getAttribute("data-font");

      // Check if the existing link has invalid weights (weights not in curated.weights)
      const existingWeightsMatch = existingDataFont?.match(/:([0-9;]+):/);
      const existingWeights = existingWeightsMatch
        ? existingWeightsMatch[1].split(";")
        : [];
      const hasInvalidWeights =
        curated.weights &&
        existingWeights.some((w) => !curated.weights!.includes(w));

      if (hasInvalidWeights) {
        anyExistingLink.remove();
        if (existingDataFont) {
          loadedFonts.delete(existingDataFont);
        }
        // Continue to load the correct version
      } else {
        loadedFonts.add(fontKey);
        return;
      }
    }

    // Build URL first to check for SSR-preloaded fonts
    const weightsParam =
      selected.length > 0 ? `:wght@${selected.join(";")}` : "";
    const subsetParam =
      normalizedSubsets && normalizedSubsets.length > 0
        ? `&subset=${normalizedSubsets.join(",")}`
        : "";
    const url = `${GOOGLE_FONTS_API}?family=${curated.family}${weightsParam}${subsetParam}&display=swap`;

    // Check if exact match exists in DOM
    const existingLink = document.querySelector(`link[data-font="${fontKey}"]`);
    if (existingLink) {
      loadedFonts.add(fontKey);
      return;
    }

    // Also check if an SSR-preloaded link with the same URL exists
    const ssrPreloadedLink = document.querySelector(`link[href="${url}"]`);
    if (ssrPreloadedLink) {
      loadedFonts.add(fontKey);
      return;
    }

    if (loadedFonts.has(fontKey)) {
      // Font in Set but not in DOM - will reload
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = url;
    link.setAttribute("data-font", fontKey);
    document.head.appendChild(link);
    loadedFonts.add(fontKey);
    return;
  }

  // Treat the input as a Google Fonts family name (e.g., "Inter", "Roboto Slab")
  const familyEncoded = encodeFamilyName(sanitizedFamily);
  // Default weights include common weights to prevent font-synthesis issues
  // and match the SSR font preloading for consistent caching
  const selected =
    normalizedWeights && normalizedWeights.length > 0
      ? normalizedWeights
      : ["400", "500", "600", "700"];
  const fontKey = `${familyEncoded}:${selected.join(";")}:${(normalizedSubsets || []).join(",")}`;

  // Build the URL to check against SSR-preloaded fonts
  const weightsParam = selected.length > 0 ? `:wght@${selected.join(";")}` : "";
  const subsetParam =
    normalizedSubsets && normalizedSubsets.length > 0
      ? `&subset=${normalizedSubsets.join(",")}`
      : "";
  const fontUrl = `${GOOGLE_FONTS_API}?family=${familyEncoded}${weightsParam}${subsetParam}&display=swap`;

  // Check if link exists in DOM first (more reliable than in-memory Set)
  // This also catches SSR-preloaded fonts that don't have data-font attribute
  const existingLink = document.querySelector(`link[data-font="${fontKey}"]`);
  if (existingLink) {
    loadedFonts.add(fontKey);
    return;
  }

  // Also check if an SSR-preloaded link with the same URL exists
  const ssrPreloadedLink = document.querySelector(`link[href="${fontUrl}"]`);
  if (ssrPreloadedLink) {
    loadedFonts.add(fontKey);
    return;
  }

  if (loadedFonts.has(fontKey)) {
    // Font in Set but not in DOM - will reload
  }

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontUrl;
  link.setAttribute("data-font", fontKey);
  document.head.appendChild(link);
  loadedFonts.add(fontKey);
}

/**
 * Preload commonly used Google Fonts
 */
export function preloadCommonFonts(): void {
  const commonFonts = [
    "roboto",
    "open-sans",
    "lato",
    "montserrat",
    "poppins",
    "inter",
  ];

  commonFonts.forEach((f) => loadGoogleFont(f));
}

// Type for font configuration
interface SectionFontConfig {
  titleConfig?: {
    fontFamily?: string;
  };
  subtitleConfig?: {
    fontFamily?: string;
  };
}

export interface FontConfig {
  typography?: {
    bodyFont?: string;
  };
  titleConfig?: {
    fontFamily?: string;
  };
  subtitleConfig?: {
    fontFamily?: string;
  };
  // Section-specific configs
  hero?: SectionFontConfig;
  features?: SectionFontConfig;
  testimonials?: SectionFontConfig;
  cta?: SectionFontConfig;
  contact?: SectionFontConfig;
  team?: SectionFontConfig;
  about?: SectionFontConfig;
  services?: SectionFontConfig;
  pricing?: SectionFontConfig;
  faq?: SectionFontConfig;
  portfolio?: SectionFontConfig;
  blog?: SectionFontConfig;
  newsletter?: SectionFontConfig;
  partners?: SectionFontConfig;
  stats?: SectionFontConfig;
  timeline?: SectionFontConfig;
  gallery?: SectionFontConfig;
  download?: SectionFontConfig;
  comparison?: SectionFontConfig;
  integration?: SectionFontConfig;
  roadmap?: SectionFontConfig;
}

/**
 * Get all Google Fonts that need to be loaded from config
 */
export interface RequiredGoogleFont {
  family: string;
  weights?: string[];
  subsets?: string[];
}

export function getRequiredGoogleFonts(
  config: FontConfig | undefined
): RequiredGoogleFont[] {
  if (!config) return [];

  const fonts = new Map<
    string,
    { family: string; weights: Set<string>; subsets: Set<string> }
  >();

  const recordFont = (
    familyValue: unknown,
    weightsValue?: unknown,
    subsetsValue?: unknown
  ) => {
    if (typeof familyValue !== "string") return;
    const sanitized = sanitizeFontFamily(familyValue);
    if (shouldSkipFont(sanitized)) return;
    const key = sanitized.toLowerCase();
    const existing = fonts.get(key);
    const entry = existing || {
      family: sanitized,
      weights: new Set<string>(),
      subsets: new Set<string>(),
    };

    const normalizedWeights = normalizeWeights(toStringArray(weightsValue));
    if (normalizedWeights) {
      normalizedWeights.forEach((w) => entry.weights.add(w));
    }

    const normalizedSubsets = normalizeSubsets(toStringArray(subsetsValue));
    if (normalizedSubsets) {
      normalizedSubsets.forEach((s) => entry.subsets.add(s));
    }

    fonts.set(key, entry);
  };

  const visited = new WeakSet<object>();

  const walk = (node: unknown) => {
    if (!node) return;
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (typeof node !== "object") return;
    if (visited.has(node as object)) return;
    visited.add(node as object);

    const obj = node as Record<string, unknown>;

    if (typeof obj.bodyFont === "string") {
      recordFont(obj.bodyFont, obj.bodyFontWeights, obj.bodyFontSubsets);
    }

    if (typeof obj.fontFamily === "string") {
      recordFont(obj.fontFamily, obj.fontWeights, obj.fontSubsets);
    }

    for (const [key, value] of Object.entries(obj)) {
      if (
        key === "bodyFontWeights" ||
        key === "bodyFontSubsets" ||
        key === "fontWeights" ||
        key === "fontSubsets"
      ) {
        continue;
      }
      walk(value);
    }
  };

  walk(config);

  return Array.from(fonts.values()).map(({ family, weights, subsets }) => ({
    family,
    weights: weights.size > 0 ? Array.from(weights).sort() : undefined,
    subsets: subsets.size > 0 ? Array.from(subsets).sort() : undefined,
  }));
}
